'use strict';

const TAG_BEST_IN_CLASS = 'best in class';
const TAG_BEST_SELLER = 'strategist hall of fame';

/**
 * Parses product filter values out of URL search params. Mostly mirrors the
 * wire format buildSearchParams() (lib/products.js) sends to the API
 * (min_price, max_price, on_sale), but drops the "tag_" prefix from
 * best_in_class/best_seller since the URL is user-facing and doesn't need to
 * expose that these filters are backed by tags internally.
 * @param {URLSearchParams} searchParams - The URL's search params to read from.
 * @returns {{price: {min: (number|null), max: (number|null)}, onSale: boolean, tags: string[]}} Partial productFilters-shaped object. Numeric fields are null when absent or malformed so callers can merge without clobbering existing state.
 */
function parseFiltersFromUrl(searchParams) {
  const minPrice = parseFloat(searchParams.get('min_price'));
  const maxPrice = parseFloat(searchParams.get('max_price'));
  const tags = [];

  if (searchParams.get('best_in_class') === 'true') tags.push(TAG_BEST_IN_CLASS);
  if (searchParams.get('best_seller') === 'true') tags.push(TAG_BEST_SELLER);

  return {
    price: {
      min: Number.isNaN(minPrice) ? null : minPrice,
      max: Number.isNaN(maxPrice) ? null : maxPrice,
    },
    onSale: searchParams.get('on_sale') === 'true',
    tags,
  };
}

/**
 * Writes product filter values onto a URL's search params — the inverse of
 * parseFiltersFromUrl(). Absent/falsy values delete their param rather than
 * writing an empty one, so shareable URLs stay free of noise params.
 * @param {URL} url - The URL object to mutate in place.
 * @param {Object} filters - A productFilters-shaped object (state.productFilters).
 * @returns {void}
 */
function writeFiltersToUrl(url, filters) {
  const { searchParams } = url;

  if (filters.price.min !== null) {
    searchParams.set('min_price', filters.price.min);
  } else {
    searchParams.delete('min_price');
  }

  if (filters.price.max !== null) {
    searchParams.set('max_price', filters.price.max);
  } else {
    searchParams.delete('max_price');
  }

  if (filters.onSale) {
    searchParams.set('on_sale', 'true');
  } else {
    searchParams.delete('on_sale');
  }

  if (filters.tags.includes(TAG_BEST_IN_CLASS)) {
    searchParams.set('best_in_class', 'true');
  } else {
    searchParams.delete('best_in_class');
  }

  if (filters.tags.includes(TAG_BEST_SELLER)) {
    searchParams.set('best_seller', 'true');
  } else {
    searchParams.delete('best_seller');
  }
}

module.exports = { parseFiltersFromUrl, writeFiltersToUrl };
